import React, { useState, useMemo, useCallback } from 'react';
import { FormControl, Select, MenuItem, TextField, Box } from '@mui/material';
import colors from 'src/utils/colors';
import SearchIcon from '@mui/icons-material/Search';
import { useSearchSelectorStore } from '../stores/searchSelectorStore';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { StacklineDefaultProfileImage } from 'src/components/SvgIcons/SvgIcons';
import AppProfileIcon from 'src/components/BeaconV2Components/Header/AppProfileIcon';
import moment from 'moment';
interface Item {
  label: string;
  value: string;
}

export interface SearchOption {
  id: string;
  label: string;
  updatedAt: string;
  updatedBy: string;
  disabled?: boolean;
}

interface MemoizedMenuItemsProps {
  items: Item[];
}
export interface SearchSelectorProps {
  options: SearchOption[];
  value: SearchOption | null;
  onChange: (option: SearchOption | null) => void;
  placeholder?: string;
}

const MemoizedMenuItems = React.memo(({ items }: MemoizedMenuItemsProps) => {
  return items.map((item) => (
    <MenuItem key={item.value} value={item.value}>
      {item.label}
    </MenuItem>
  ));
});
MemoizedMenuItems.displayName = 'MemoizedMenuItems';

const SearchMenuItem = React.memo(({ placeholder }: { placeholder?: string }) => {
  const { searchValue, setSearchValue } = useSearchSelectorStore();

  // Stop propagation of all events to prevent them from reaching the Select component
  const handleSearchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSearchValue(e.target.value);
  };

  return (
    <MenuItem
      sx={{
        padding: '12px',
        width: '100%',
        backgroundColor: '#F2F4F6 !important',
        cursor: 'default',
        pointerEvents: 'auto',
        '&:hover': {
          backgroundColor: '#F2F4F6 !important'
        },
        '&:focus': {
          backgroundColor: '#F2F4F6 !important'
        },
        '&.Mui-focusVisible': {
          backgroundColor: '#F2F4F6 !important'
        }
      }}
      onClick={handleSearchClick}
      disableRipple={true}
      dense={true}
      autoFocus={false}
    >
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <SearchIcon
          sx={{
            color: colors.searchGreyLight,
            fontSize: '20px'
          }}
        />
        <TextField
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          fullWidth
          onClick={handleSearchClick}
          onKeyDown={handleKeyDown}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              backgroundColor: 'transparent !important',
              '& input': {
                color: colors.searchGrey,
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                padding: '0 !important',
                marginLeft: '8px'
              },
              '& input::placeholder': {
                color: colors.searchGrey,
                opacity: 1,
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400
              }
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            }
          }}
        />
      </Box>
    </MenuItem>
  );
});
SearchMenuItem.displayName = 'SearchMenuItem';

const OptionMenuItem = React.memo(
  ({ option, onSelect }: { option: SearchOption; onSelect: (option: SearchOption) => void }) => {
    const { selectedOption } = useSearchSelectorStore();
    const isSelected = selectedOption?.id === option.id;
    const users = useAppSelector((state) => state.user.team);
    const user = users.find((user) => user.id === option.updatedBy);

    const renderIcon = useCallback(() => {
      if (user?.id && user?.firstName && user?.lastName) {
        return <AppProfileIcon userId={user.id} initials={`${user.firstName[0]}${user.lastName[0]}`} />;
      }
      return <StacklineDefaultProfileImage style={{ width: '30px', height: '30px' }} />;
    }, [user]);

    return (
      <MenuItem
        onClick={() => onSelect(option)}
        selected={isSelected}
        sx={{
          color: colors.darkBlue,
          fontSize: '14px',
          fontWeight: 400,
          borderBottom: `1px solid ${colors.graysSecondary}`,
          '&:hover': {
            backgroundColor: '#E4F1FF !important'
          },
          '&.Mui-selected': {
            backgroundColor: '#E4F1FF !important'
          },
          '&.Mui-selected:hover': {
            backgroundColor: '#E4F1FF !important'
          },
          '&.Mui-focusVisible': {
            backgroundColor: '#E4F1FF !important'
          }
        }}
        disableRipple
      >
        <Flex
          justifyContent="space-between"
          sx={{
            width: '100%'
          }}
        >
          <Flex flexDirection="column">
            <Text
              variant="h7"
              sx={{
                fontWeight: 500
              }}
            >
              {option.label}
            </Text>
            <Text variant="body2">{`Last edited ${moment(option.updatedAt).format('MMM D, YYYY')}`}</Text>
          </Flex>
          <Flex>
            <Flex
              flexDirection="column"
              sx={{
                marginRight: '8px',
                marginTop: 'auto',
                marginBottom: 'auto'
              }}
            >
              <Text
                variant="h7"
                sx={{
                  textAlign: 'right',
                  fontSize: '12px',
                  fontWeight: 500
                }}
              >
                {user?.firstName && user?.lastName ? `${user?.firstName} ${user?.lastName}` : 'Stackline'}
              </Text>
              <Text
                variant="body3"
                sx={{
                  textAlign: 'right'
                }}
              >
                {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Support'}
              </Text>
            </Flex>
            {renderIcon()}
          </Flex>
        </Flex>
      </MenuItem>
    );
  }
);
OptionMenuItem.displayName = 'OptionMenuItem';

const SearchSelector: React.FC<SearchSelectorProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Search content profiles'
}) => {
  const [open, setOpen] = useState(false);
  const { searchValue, setSelectedOption, resetSearch } = useSearchSelectorStore();

  const handleClose = useCallback(() => {
    setOpen(false);
    resetSearch();
  }, [resetSearch]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    return options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()));
  }, [options, searchValue]);

  const handleOptionSelect = useCallback(
    (option: SearchOption) => {
      onChange(option);
      setSelectedOption(option);
      handleClose();
    },
    [onChange, setSelectedOption, handleClose]
  );

  return (
    <FormControl fullWidth>
      <Select
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        value={value?.id || ''}
        displayEmpty
        renderValue={() => value?.label || ''}
        onKeyDown={handleKeyDown}
        native={false}
        sx={{
          height: '40px',
          '& .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${colors.graysSecondary}`
          },
          '& .MuiSelect-select': {
            color: colors.darkBlue,
            fontSize: '14px !important',
            fontWeight: 400,
            padding: '6px 12px',
            margin: '0 !important'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${colors.graysSecondary}`
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${colors.graysSecondary}`,
            boxShadow: 'none'
          }
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: '300px',
              overflowY: 'auto',
              borderRadius: '8px',
              marginTop: '8px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              '&::-webkit-scrollbar': {
                width: '0px'
              },
              '& .MuiList-root': {
                padding: 0,
                width: '658px'
              }
            }
          },
          sx: {
            '& .MuiMenuItem-root': {
              padding: '8px 12px',
              '&:focus': {
                backgroundColor: 'transparent'
              },
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent'
              }
            }
          },
          disableAutoFocusItem: true,
          autoFocus: false
        }}
      >
        <SearchMenuItem placeholder={placeholder} />
        {filteredOptions.map((option) => (
          <OptionMenuItem key={option.id} option={option} onSelect={handleOptionSelect} />
        ))}
      </Select>
    </FormControl>
  );
};

export default SearchSelector;
